import { BrowserModule } from '@angular/platform-browser';
import { NgModule, APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { HeaderModule, HttpInterceptorService, NewFeatureAlertModule, IndexTickerModule } from './_shared';
import { NgBackdropModule } from './_shared';
import { SideBarModule } from './_shared/modules/side-bar/side-bar.module';
import { NgxEchartsModule } from 'ngx-echarts';
import * as echarts from 'echarts';


import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ConfigurationService } from './_shared';
import { OwlDateTimeModule, OwlNativeDateTimeModule, OWL_DATE_TIME_FORMATS, OWL_DATE_TIME_LOCALE } from '@danielmoncada/angular-datetime-picker';
import { MAT_DATE_LOCALE } from '@angular/material/core';


export const MY_NATIVE_FORMATS = {
  fullPickerInput: { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric' },
  datePickerInput: { year: 'numeric', month: 'numeric', day: 'numeric' },
  timePickerInput: { hour: 'numeric', minute: 'numeric', second: 'numeric' },
  monthYearLabel: { year: 'numeric', month: 'short' },
  dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
  monthYearA11yLabel: { year: 'numeric', month: 'long' },
};
import { MatBadgeModule } from '@angular/material/badge';
import { ToastrModule } from 'ngx-toastr';
import { MatIconModule } from '@angular/material/icon';
import { MatSidenavModule } from '@angular/material/sidenav';
import { NotificationsModule } from './modules/trading/modules/profile/components/notifications/notifications.module';
import { FormsModule } from '@angular/forms';
import { CustomToasterModule } from './_shared/modules/custom-toaster/custom-toaster.module';
import { FailoverWarningModule } from './_shared/modules/failover-warning/failover-warning.module';
import { PromotePlanModule } from './_shared/modules/promote-plan/promote-plan.module';
import { BrokerPlanPaymentFailedModule } from './_shared/modules/broker-plan-payment-failed/broker-plan-payment-failed.module';
import { LengthLimiterModule } from './_shared/directives/length-limiter/length-limiter.module';
import { UploadPortfolioModule } from './_shared/modules/upload-portfolio/upload-portfolio.module';
import { NgOtpInputModule } from 'ng-otp-input';
import { BrokerRenewalSuccessModule } from './_shared/modules/broker-renewal-success/broker-renewal-success/broker-renewal-success.module';
import { FreePlanExtendModule } from './_shared/modules/free-plan-extend/free-plan-extend.module';
import { FixWithAiToastModule } from './modules/trading/modules/create-with-ai/fix-with-ai-toast/fix-with-ai-toast.module';

import { register } from 'swiper/element/bundle';
import { LottieModule } from 'ngx-lottie';
import player from 'lottie-web';
import { RouteReuseStrategy } from '@angular/router';
import { CustomReuseStrategy } from './app.route-strategy';
import { CreditsExhaustedPopupModule } from './_shared/modules/credits-exhausted-popup/credits-exhausted-popup.module';
import { MatLegacyDialogModule as MatDialogModule } from '@angular/material/legacy-dialog';
import {
  SocialLoginModule,
  SocialAuthServiceConfig,
  GoogleLoginProvider
} from '@abacritt/angularx-social-login';

/**
 * Google Sign-In (@abacritt/angularx-social-login v2.1.0, Angular 16).
 * Client ID is read from Firebase Remote Config → SOCIAL_LOGIN_CONFIG.google.clientId
 * (loaded into remoteConfigCacheObj before bootstrap via APP_INITIALIZER).
 * Registered in AppModule so initial-user-details and auth routes can both use it.
 * Note: library SocialAuthService is aliased as NgxSocialAuthService in google.provider.ts
 * to avoid clashing with our custom SocialAuthService orchestrator.
 */
export function socialAuthConfigFactory(config: ConfigurationService): SocialAuthServiceConfig {
  return {
    autoLogin: false,
    providers: config.remoteConfigCacheObj?.SOCIAL_LOGIN_CONFIG?.google?.clientId ? [{
      id: GoogleLoginProvider.PROVIDER_ID,
      provider: new GoogleLoginProvider(config.remoteConfigCacheObj?.SOCIAL_LOGIN_CONFIG?.google?.clientId, {
        oneTapEnabled: false,
        scopes: 'openid profile email',
      })
    }] : [],
    onError: (err) => console.error('Google social login error', err)
  };
}

register();


export function playerFactory() {
  return player;
}

@NgModule({
  declarations: [
    AppComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    HttpClientModule,
    NgBackdropModule,
    OwlDateTimeModule,
    MatIconModule,
    MatSidenavModule,
    OwlNativeDateTimeModule,
    NotificationsModule,
    FormsModule,
    CustomToasterModule,
    FixWithAiToastModule,
    FailoverWarningModule,
    PromotePlanModule,
    BrokerPlanPaymentFailedModule,
    FreePlanExtendModule,
    BrokerRenewalSuccessModule,
    UploadPortfolioModule,
    HeaderModule,
    SideBarModule,
    IndexTickerModule,
    NgxEchartsModule.forRoot({
      echarts
    }),
    MatBadgeModule,
    ToastrModule.forRoot({
      positionClass: 'toast-bottom-right',
      toastClass: 'ngx-toastr custom-snackbar'
    }),
    LengthLimiterModule,
    NgOtpInputModule,
    NewFeatureAlertModule,
    // PlanAlertModule,
    LottieModule.forRoot({
      player: playerFactory
  }),
    CreditsExhaustedPopupModule,
    MatDialogModule,
    // Google Identity Services — see socialAuthConfigFactory above
    SocialLoginModule,
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpInterceptorService,
      multi: true
    },
    ConfigurationService,
    {
      provide: APP_INITIALIZER,
      useFactory: (configService: ConfigurationService) => () => configService.loadConfigurationFile(),
      deps: [ConfigurationService],
      multi: true
    },
    {
      provide: RouteReuseStrategy,
      useClass: CustomReuseStrategy
    },
    {
      provide: OWL_DATE_TIME_FORMATS,
      useValue: MY_NATIVE_FORMATS
    },
    {
      provide: OWL_DATE_TIME_LOCALE,
      useValue: 'en-US'
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: 'en-GB'
    },
    {
      provide: 'SocialAuthServiceConfig',
      useFactory: socialAuthConfigFactory,
      deps: [ConfigurationService]
    },
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule { }
